import { beschriftung, gebietsschema, periodenkuerzel, t } from './sprache'
import type { Berichtswelt } from './typen'

/**
 * **Aufeinanderfolgende Monate als Spanne** — zwölf Monate einzeln wären eine
 * Zeile, die niemand liest: *Jan. 2026 – Dez. 2026* statt zwölf Kürzeln.
 */
export function spannen(perioden: number[]): string[] {
  const sortiert = [...new Set(perioden)].sort((a, b) => a - b)
  const folgt = (a: number, b: number) => (a % 100 === 12 ? b === (Math.floor(a / 100) + 1) * 100 + 1 : b === a + 1)
  const teile: string[] = []

  for (let i = 0; i < sortiert.length; ) {
    let j = i
    while (j + 1 < sortiert.length && folgt(sortiert[j]!, sortiert[j + 1]!)) {
      j++
    }

    teile.push(
      j > i
        ? `${periodenkuerzel(sortiert[i]!)} – ${periodenkuerzel(sortiert[j]!)}`
        : periodenkuerzel(sortiert[i]!),
    )
    i = j + 1
  }

  return teile
}

/**
 * **Aus welcher Welt der Bericht kommt** (2.13 Abschnitt 14, R1111–R1114).
 *
 * Der Auftraggeber am 2026-10-05: *„Es gibt zwei Welten: Die Rohdaten der
 * Einzelgesellschaften — so wie sie sind. … Dann gibt es den
 * Konzernabschlussprozess … Diese Daten ändern sich nur, wenn der KA-Prozess
 * angestoßen wird."*
 *
 * Beide Welten gab es schon, aber ohne Namen. Am selben Tag stand eine
 * gelöschte Zeile weiter in der Kapitalflussrechnung des Konzerns, weil der
 * Konzernabschluss nicht neu gerechnet war — richtig so, aber nirgends gesagt.
 * Diese Zeile sagt es: welche Welt, von wann, was seither geändert ist, und
 * wohin der Weg führt.
 */
export function Weltzeile({
  geliefert,
  welt,
  kreis,
  ledger,
  kopf = true,
}: {
  /** Oben ist eine Gesellschaft gewählt — dann sind es die Lieferdaten. */
  geliefert: boolean
  welt: Berichtswelt | null
  kreis: string
  ledger: string
  /**
   * Die Welt selbst steht in dieser Zeile. Wo der Bericht eine Reihe der
   * Spaltenansicht hat, steht sie dort im {@link Weltkopf} (R1313), und hier
   * bleiben nur die Sätze, die etwas verlangen.
   */
  kopf?: boolean
}) {
  if (geliefert) {
    return kopf ? <Weltkopf geliefert welt={null} className="weltzeile lieferdaten" /> : null
  }

  if (!welt) {
    return null
  }

  const name = weltname(welt)
  const monate = (liste: number[]) => spannen(liste).join(', ')

  // **Der Weg weiter** (die Auflage zu R1112): zum Konzernabschluss, der den
  // Zeitraum umfasst — sonst zum Anlegen, mit Kreis, Bewertungsebene und
  // Geschäftsjahresbeginn schon eingetragen.
  const zumRechnen = welt.konzernabschluss
    ? `#/abschluss/konzernabschluesse?rechnen=${encodeURIComponent(welt.konzernabschluss)}`
    : null
  const zumAnlegen = `#/abschluss/konzernabschluesse?${new URLSearchParams({
    anlegen: kreis,
    ledger,
    von: String(welt.jahresbeginn),
  })}`

  const daten = monate(welt.datenSeither)
  const stammdaten = monate(welt.stammdatenSeither)
  const seither =
    daten && daten === stammdaten
      ? [t('welt.beides', { monate: daten })]
      : [
          ...(daten ? [t('welt.daten', { monate: daten })] : []),
          ...(stammdaten ? [t('welt.stammdaten', { monate: stammdaten })] : []),
        ]

  if (!kopf && welt.ohneLauf.length === 0 && seither.length === 0) {
    return null
  }

  return (
    <div className="weltzeile konzernabschluss">
      {kopf && <Weltkopf geliefert={false} welt={welt} />}

      {welt.ohneLauf.length > 0 && (
        <p className="weltzeile-hinweis">
          {t('welt.ohnelauf', { monate: monate(welt.ohneLauf) })}{' '}
          {welt.angelegt && zumRechnen ? (
            <a href={zumRechnen}>{t('welt.weg.oeffnen', { name })}</a>
          ) : (
            <a href={zumAnlegen}>{t('welt.weg.anlegen')}</a>
          )}
        </p>
      )}

      {/*
        **Ein Satz, ein Weg.** Betreffen Daten und Stammdaten dieselben Monate,
        steht beides in einem Satz; sonst zwei Sätze — der Weg zum Neurechnen
        steht in jedem Fall einmal, am Ende.
      */}
      {seither.length > 0 && (
        <p className="weltzeile-hinweis">
          {seither.join(' ')}{' '}
          {zumRechnen && <a href={zumRechnen}>{t('welt.weg.rechnen', { name })}</a>}
        </p>
      )}
    </div>
  )
}

function weltname(welt: Berichtswelt): string {
  return (welt.name && beschriftung(welt.name)) || t('kontext.welt.konzernabschluss')
}

/**
 * **Die Welt in einer Zeile** — *Lieferdaten · Stand jetzt* oder
 * *Konzernabschluss 2026 · gerechnet am …*.
 *
 * **Sie steht in der Reihe der Spaltenansicht, rechts neben den Knöpfen**
 * (2.13 Abschnitt 14.6, R1313). Der Auftraggeber am 2026-10-06: *„Dieser
 * Eintrag nimmt zu viel Platz weg."* — allein über dem Bericht kostete sie
 * eine ganze Zeile, während daneben Platz frei war.
 */
export function Weltkopf({
  geliefert,
  welt,
  className = 'weltkopf',
}: {
  geliefert: boolean
  welt: Berichtswelt | null
  className?: string
}) {
  if (geliefert) {
    return (
      <p className={className}>
        <strong>{t('kontext.welt.lieferdaten')}</strong> · {t('welt.standjetzt')}
      </p>
    )
  }

  if (!welt?.gerechnetAm) {
    return null
  }

  const zeitpunkt = new Intl.DateTimeFormat(gebietsschema(), { dateStyle: 'long', timeStyle: 'short' }).format(
    new Date(welt.gerechnetAm),
  )

  return (
    <p className={className}>
      <strong>{weltname(welt)}</strong> · {t('welt.gerechnet', { zeitpunkt })}
    </p>
  )
}
